/**
 * KS-PF2 — Lifecycle Governance Page
 * Route: /app/lifecycle
 *
 * Four tabs:
 *   1. Retention Policies   — versioned governance records (DRAFT → ACTIVE → SUPERSEDED)
 *   2. Legal Holds          — placement and release of active holds
 *   3. Deletion Requests    — full governed deletion workflow
 *   4. Tombstones           — read-only lifecycle evidence after deletion
 *
 * Role-based UI:
 *   ORG_ADMIN         — full administration
 *   CANON_OWNER       — canon-protection controls + hold management
 *   KNOWLEDGE_STEWARD — deletion requests, read lifecycle state
 *   REVIEWER          — read-only
 *   AUDITOR           — read-only
 *
 * Frontend role gating is UX only. Backend RBAC remains authoritative.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Shield, Lock, FileX, Archive, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle, Clock, XCircle, Plus, Eye,
} from 'lucide-react';
import api from '@/lib/api';
import { session } from '@/lib/session';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return d; }
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { dateStyle: 'medium' }); }
  catch { return d; }
};

function Badge({ label, color = 'gray', testId }) {
  const styles = {
    green:  { backgroundColor: '#dcfce7', color: '#166534' },
    red:    { backgroundColor: '#fee2e2', color: '#991b1b' },
    amber:  { backgroundColor: '#fef3c7', color: '#92400e' },
    blue:   { backgroundColor: '#dbeafe', color: '#1e40af' },
    gray:   { backgroundColor: '#f3f4f6', color: '#374151' },
    black:  { backgroundColor: '#000',    color: '#03f37c' },
  };
  return (
    <span
      className="font-heading font-semibold px-2 py-0.5 rounded"
      style={{ fontSize: '10px', letterSpacing: '0.06em', ...(styles[color] || styles.gray) }}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

function statusBadge(status) {
  const map = {
    DRAFT: { label: 'DRAFT', color: 'gray' },
    ACTIVE: { label: 'ACTIVE', color: 'green' },
    SUPERSEDED: { label: 'SUPERSEDED', color: 'amber' },
    'PENDING_APPROVAL': { label: 'PENDING APPROVAL', color: 'blue' },
    APPROVED: { label: 'APPROVED', color: 'green' },
    EXECUTING: { label: 'EXECUTING', color: 'blue' },
    COMPLETED: { label: 'COMPLETED', color: 'black' },
    FAILED: { label: 'FAILED', color: 'red' },
    CANCELLED: { label: 'CANCELLED', color: 'gray' },
    BLOCKED_BY_HOLD: { label: 'BLOCKED BY HOLD', color: 'red' },
    'PLAN_CHANGED_REAPPROVAL_REQUIRED': { label: 'REAPPROVAL REQUIRED', color: 'amber' },
    REQUESTED: { label: 'REQUESTED', color: 'blue' },
  };
  const entry = map[status] || { label: status, color: 'gray' };
  return <Badge label={entry.label} color={entry.color} />;
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h2 className="font-heading font-semibold text-black" style={{ fontSize: '16px', letterSpacing: '0.02em' }}>
        {title}
      </h2>
      {subtitle && (
        <p className="font-body text-gray-500 mt-1" style={{ fontSize: '12px' }}>{subtitle}</p>
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="rounded border border-dashed p-8 text-center" style={{ borderColor: '#e5e5e5' }}>
      <p className="font-body text-gray-400" style={{ fontSize: '13px' }}>{message}</p>
    </div>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div className="rounded p-3 mb-4" style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5' }}>
      <p className="font-body text-red-700" style={{ fontSize: '12px' }}>{msg}</p>
    </div>
  );
}

function SuccessMsg({ msg }) {
  if (!msg) return null;
  return (
    <div className="rounded p-3 mb-4" style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
      <p className="font-body text-green-700" style={{ fontSize: '12px' }}>{msg}</p>
    </div>
  );
}

// ─── Role helpers ─────────────────────────────────────────────────────────────

const ADMIN_ROLES = ['ORG_ADMIN'];
const APPROVAL_ROLES = ['ORG_ADMIN', 'CANON_OWNER'];
const REQUEST_ROLES = ['ORG_ADMIN', 'CANON_OWNER', 'KNOWLEDGE_STEWARD'];
const HOLD_MANAGE_ROLES = ['ORG_ADMIN', 'CANON_OWNER'];

function useRole() {
  const user = session.getUser();
  const role = user?.role || '';
  return {
    role,
    canManagePolicy: ADMIN_ROLES.includes(role),
    canManageHold: HOLD_MANAGE_ROLES.includes(role),
    canRequest: REQUEST_ROLES.includes(role),
    canApprove: APPROVAL_ROLES.includes(role),
    canExecute: ADMIN_ROLES.includes(role),
  };
}

// ─── Tab: Retention Policies ──────────────────────────────────────────────────

function RetentionPoliciesTab() {
  const { canManagePolicy } = useRole();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', resource_class: 'artifact',
    retention_duration_days: '', effective_date: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/lifecycle/retention-policies');
      setPolicies(r.data.policies || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load retention policies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setSubmitting(true); setErr(''); setSuccess('');
    try {
      await api.post('/lifecycle/retention-policies', {
        ...form,
        retention_duration_days: form.retention_duration_days === ''
          ? null : parseInt(form.retention_duration_days, 10),
      });
      setSuccess('Retention policy created as DRAFT.');
      setShowCreate(false);
      setForm({ name: '', description: '', resource_class: 'artifact', retention_duration_days: '', effective_date: '' });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to create policy.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleActivate(policyId) {
    setErr(''); setSuccess('');
    try {
      await api.post(`/lifecycle/retention-policies/${policyId}/activate`);
      setSuccess('Policy activated. Prior active policy superseded.');
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Activation failed.');
    }
  }

  return (
    <div data-testid="retention-policies-tab">
      <SectionHeader
        title="Retention Policies"
        subtitle="Versioned governance records. DRAFT policies have no effect. ACTIVE policy governs retention evaluation. A new activation supersedes the previous active version — all versions are preserved."
      />
      <ErrorMsg msg={err} />
      <SuccessMsg msg={success} />

      {canManagePolicy && (
        <div className="mb-6">
          <button
            onClick={() => setShowCreate(v => !v)}
            className="inline-flex items-center gap-2 font-heading font-semibold px-4 py-2 rounded"
            style={{ backgroundColor: '#000', color: '#03f37c', fontSize: '12px' }}
            data-testid="create-policy-btn"
          >
            <Plus size={13} />
            Create Draft Policy
          </button>
        </div>
      )}

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="mb-8 p-5 rounded border"
          style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }}
          data-testid="create-policy-form"
        >
          <h3 className="font-heading font-semibold text-black mb-4" style={{ fontSize: '13px', letterSpacing: '0.04em' }}>
            NEW DRAFT RETENTION POLICY
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>NAME</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="policy-name-input" />
            </div>
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>RESOURCE CLASS</label>
              <select value={form.resource_class} onChange={e => setForm(f => ({ ...f, resource_class: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body bg-white" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="policy-resource-class-select">
                <option value="artifact">artifact</option>
                <option value="claim">claim</option>
                <option value="constraint">constraint</option>
              </select>
            </div>
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>
                RETENTION DAYS <span className="font-normal text-gray-400">(leave blank for indefinite)</span>
              </label>
              <input type="number" min="1" value={form.retention_duration_days}
                onChange={e => setForm(f => ({ ...f, retention_duration_days: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="policy-retention-days-input" />
            </div>
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>EFFECTIVE DATE</label>
              <input required type="date" value={form.effective_date}
                onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="policy-effective-date-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>DESCRIPTION</label>
              <textarea required value={form.description} rows={2}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6', resize: 'vertical' }}
                data-testid="policy-description-input" />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="font-heading font-semibold px-4 py-2 rounded"
              style={{ backgroundColor: '#000', color: '#fff', fontSize: '12px' }}
              data-testid="policy-submit-btn">
              {submitting ? 'Creating…' : 'Create Draft Policy'}
            </button>
            <button type="button" onClick={() => setShowCreate(false)}
              className="font-heading font-medium px-4 py-2 rounded border"
              style={{ fontSize: '12px', borderColor: '#d9dee6' }}
              data-testid="policy-cancel-btn">
              Cancel
            </button>
          </div>
          <p className="font-body text-gray-400 mt-3" style={{ fontSize: '11px' }}>
            Policy starts as DRAFT and has no governance effect until explicitly activated.
          </p>
        </form>
      )}

      {loading ? (
        <p className="font-body text-gray-400" style={{ fontSize: '13px' }} data-testid="policies-loading">Loading…</p>
      ) : policies.length === 0 ? (
        <EmptyState message="No retention policies defined. Create a draft policy to begin." />
      ) : (
        <div className="space-y-3" data-testid="policies-list">
          {policies.map(p => (
            <div key={p.policy_id} className="rounded border p-4" style={{ borderColor: '#e5e5e5' }} data-testid={`policy-item-${p.policy_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-heading font-semibold text-black" style={{ fontSize: '13px' }}>{p.name}</span>
                    {statusBadge(p.status)}
                    <span className="font-heading text-gray-400" style={{ fontSize: '10px' }}>v{p.policy_version}</span>
                  </div>
                  <p className="font-body text-gray-600" style={{ fontSize: '12px' }}>{p.description}</p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>class: {p.resource_class}</span>
                    <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>
                      retention: {p.retention_duration_days ? `${p.retention_duration_days}d` : 'INDEFINITE'}
                    </span>
                    <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>
                      effective: {fmtDate(p.effective_date)}
                    </span>
                    <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>
                      created: {fmt(p.created_at)}
                    </span>
                  </div>
                </div>
                {canManagePolicy && p.status === 'DRAFT' && (
                  <button
                    onClick={() => handleActivate(p.policy_id)}
                    className="font-heading font-semibold px-3 py-1.5 rounded border shrink-0"
                    style={{ fontSize: '11px', borderColor: '#000', backgroundColor: '#000', color: '#03f37c' }}
                    data-testid={`activate-policy-btn-${p.policy_id}`}
                  >
                    Activate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Legal Holds ─────────────────────────────────────────────────────────

function LegalHoldsTab() {
  const { canManageHold } = useRole();
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [showPlace, setShowPlace] = useState(false);
  const [form, setForm] = useState({ scope: 'organization', resource_type: '', resource_id: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/lifecycle/legal-holds');
      setHolds(r.data.holds || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load holds.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handlePlace(e) {
    e.preventDefault();
    setSubmitting(true); setErr(''); setSuccess('');
    try {
      const body = { scope: form.scope, reason: form.reason };
      if (form.scope !== 'organization') {
        body.resource_type = form.resource_type || form.scope;
        body.resource_id = form.resource_id;
      }
      await api.post('/lifecycle/legal-holds', body);
      setSuccess('Legal hold placed. All affected deletion requests are now blocked.');
      setShowPlace(false);
      setForm({ scope: 'organization', resource_type: '', resource_id: '', reason: '' });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to place hold.');
    } finally { setSubmitting(false); }
  }

  async function handleRelease(holdId) {
    const reason = window.prompt('Release reason (required):');
    if (!reason?.trim()) return;
    setErr(''); setSuccess('');
    try {
      await api.post(`/lifecycle/legal-holds/${holdId}/release`, { release_reason: reason });
      setSuccess('Hold released.');
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to release hold.');
    }
  }

  const activeHolds = holds.filter(h => h.status === 'ACTIVE');
  const releasedHolds = holds.filter(h => h.status === 'RELEASED');

  return (
    <div data-testid="legal-holds-tab">
      <SectionHeader
        title="Legal Holds"
        subtitle="An ACTIVE hold overrides retention eligibility and blocks all deletion requests for the affected resource — even those already approved."
      />
      <ErrorMsg msg={err} />
      <SuccessMsg msg={success} />

      {canManageHold && (
        <div className="mb-6">
          <button
            onClick={() => setShowPlace(v => !v)}
            className="inline-flex items-center gap-2 font-heading font-semibold px-4 py-2 rounded"
            style={{ backgroundColor: '#000', color: '#03f37c', fontSize: '12px' }}
            data-testid="place-hold-btn"
          >
            <Lock size={13} />
            Place Legal Hold
          </button>
        </div>
      )}

      {showPlace && (
        <form onSubmit={handlePlace} className="mb-8 p-5 rounded border"
          style={{ borderColor: '#fca5a5', backgroundColor: '#fff5f5' }}
          data-testid="place-hold-form">
          <h3 className="font-heading font-semibold text-black mb-4" style={{ fontSize: '13px', letterSpacing: '0.04em' }}>
            PLACE LEGAL HOLD
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>SCOPE</label>
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body bg-white" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="hold-scope-select">
                <option value="organization">Organization-wide</option>
                <option value="artifact">Specific artifact</option>
                <option value="claim">Specific claim</option>
                <option value="constraint">Specific constraint</option>
              </select>
            </div>
            {form.scope !== 'organization' && (
              <div>
                <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>RESOURCE ID</label>
                <input required={form.scope !== 'organization'} value={form.resource_id}
                  onChange={e => setForm(f => ({ ...f, resource_id: e.target.value }))}
                  placeholder={`${form.scope} ID…`}
                  className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                  data-testid="hold-resource-id-input" />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>REASON (required)</label>
              <textarea required value={form.reason} rows={2}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Legal or governance reason for this hold…"
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6', resize: 'vertical' }}
                data-testid="hold-reason-input" />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="font-heading font-semibold px-4 py-2 rounded"
              style={{ backgroundColor: '#dc2626', color: '#fff', fontSize: '12px' }}
              data-testid="hold-submit-btn">
              {submitting ? 'Placing…' : 'Confirm — Place Hold'}
            </button>
            <button type="button" onClick={() => setShowPlace(false)}
              className="font-heading font-medium px-4 py-2 rounded border"
              style={{ fontSize: '12px', borderColor: '#d9dee6' }}
              data-testid="hold-cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="font-body text-gray-400" style={{ fontSize: '13px' }} data-testid="holds-loading">Loading…</p>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="font-heading font-semibold text-black mb-3" style={{ fontSize: '12px', letterSpacing: '0.05em' }}>
              ACTIVE HOLDS ({activeHolds.length})
            </h3>
            {activeHolds.length === 0 ? (
              <EmptyState message="No active legal holds." />
            ) : (
              <div className="space-y-3" data-testid="active-holds-list">
                {activeHolds.map(h => (
                  <div key={h.hold_id} className="rounded border p-4" style={{ borderColor: '#fca5a5', backgroundColor: '#fff5f5' }} data-testid={`hold-item-${h.hold_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge label="ACTIVE HOLD" color="red" />
                          <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>{h.hold_id}</span>
                        </div>
                        <p className="font-body text-gray-800 font-medium" style={{ fontSize: '13px' }}>{h.reason}</p>
                        <div className="mt-1 flex flex-wrap gap-4">
                          <span className="font-body text-gray-500" style={{ fontSize: '11px' }}>scope: {h.scope}</span>
                          {h.resource_id && <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>resource: {h.resource_id}</span>}
                          <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>placed: {fmt(h.placed_at)}</span>
                        </div>
                      </div>
                      {canManageHold && (
                        <button
                          onClick={() => handleRelease(h.hold_id)}
                          className="font-heading font-semibold px-3 py-1.5 rounded border shrink-0"
                          style={{ fontSize: '11px', borderColor: '#dc2626', color: '#dc2626' }}
                          data-testid={`release-hold-btn-${h.hold_id}`}
                        >
                          Release
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {releasedHolds.length > 0 && (
            <div>
              <h3 className="font-heading font-semibold text-black mb-3" style={{ fontSize: '12px', letterSpacing: '0.05em' }}>
                RELEASED HOLDS ({releasedHolds.length})
              </h3>
              <div className="space-y-2" data-testid="released-holds-list">
                {releasedHolds.map(h => (
                  <div key={h.hold_id} className="rounded border p-3" style={{ borderColor: '#e5e5e5' }} data-testid={`hold-item-${h.hold_id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge label="RELEASED" color="gray" />
                      <span className="font-body text-gray-600" style={{ fontSize: '12px' }}>{h.reason}</span>
                      <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>{h.hold_id}</span>
                    </div>
                    <p className="font-body text-gray-400 mt-1" style={{ fontSize: '11px' }}>
                      Released: {fmt(h.released_at)} — {h.release_reason}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab: Deletion Requests ───────────────────────────────────────────────────

function ImpactPlanPanel({ requestId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function loadPlan() {
    setLoading(true); setErr('');
    try {
      const r = await api.get(`/lifecycle/deletion-requests/${requestId}/plan`);
      setPlan(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load plan.');
    } finally { setLoading(false); }
  }

  if (!plan && !loading) {
    return (
      <button
        onClick={loadPlan}
        className="font-heading font-medium text-gray-500 hover:text-black text-xs flex items-center gap-1"
        data-testid="load-impact-plan-btn"
        style={{ fontSize: '11px' }}
      >
        <Eye size={11} /> View impact plan
      </button>
    );
  }

  if (loading) return <p className="font-mono text-gray-400" style={{ fontSize: '11px' }} data-testid="plan-loading">Loading plan…</p>;
  if (err) return <p className="font-body text-red-500" style={{ fontSize: '11px' }}>{err}</p>;

  return (
    <div className="mt-3 rounded p-3 border" style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }} data-testid="impact-plan-panel">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <span className="font-heading font-semibold text-black" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>IMPACT PLAN</span>
        {plan.plan_changed && <Badge label="PLAN CHANGED — REAPPROVAL REQUIRED" color="amber" testId="plan-changed-badge" />}
      </div>
      <div className="space-y-1">
        <p className="font-mono text-gray-400" style={{ fontSize: '10px' }}>
          fingerprint: {plan.stored_fingerprint?.slice(0, 16)}…
          {plan.plan_changed && <span className="text-amber-600 ml-2">≠ current: {plan.current_fingerprint?.slice(0, 16)}…</span>}
        </p>
        <p className="font-body text-gray-600" style={{ fontSize: '11px' }}>
          Records to delete: {plan.plan?.records_to_delete?.length ?? '—'}
          {' '}| Preserved: {plan.plan?.records_preserved?.length ?? '—'}
          {' '}| Files: {plan.plan?.stored_files?.length ?? '—'}
        </p>
        {plan.plan?.requires_canon_approval && (
          <Badge label="CANON OWNER APPROVAL REQUIRED" color="amber" testId="canon-required-badge" />
        )}
      </div>
    </div>
  );
}

function DeletionRequestsTab() {
  const { canRequest, canApprove, canExecute, role } = useRole();
  const user = session.getUser();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ target_resource_type: 'artifact', target_resource_id: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/lifecycle/deletion-requests');
      setRequests(r.data.requests || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load deletion requests.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setSubmitting(true); setErr(''); setSuccess('');
    try {
      await api.post('/lifecycle/deletion-requests', form);
      setSuccess('Deletion request submitted. Pending approval.');
      setShowCreate(false);
      setForm({ target_resource_type: 'artifact', target_resource_id: '', reason: '' });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to create deletion request.');
    } finally { setSubmitting(false); }
  }

  async function handleApprove(reqId) {
    setActioning(reqId); setErr(''); setSuccess('');
    try {
      const r = await api.post(`/lifecycle/deletion-requests/${reqId}/approve`, { notes: 'Governance approval' });
      setSuccess(`Approval recorded. Status: ${r.data.status}`);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Approval failed.');
    } finally { setActioning(null); }
  }

  async function handleCancel(reqId) {
    if (!window.confirm('Cancel this deletion request?')) return;
    setActioning(reqId); setErr(''); setSuccess('');
    try {
      await api.post(`/lifecycle/deletion-requests/${reqId}/cancel`);
      setSuccess('Deletion request cancelled.');
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Cancel failed.');
    } finally { setActioning(null); }
  }

  async function handleExecute(reqId) {
    if (!window.confirm('Execute this deletion? This action is irreversible.')) return;
    setActioning(reqId); setErr(''); setSuccess('');
    try {
      const r = await api.post(`/lifecycle/deletion-requests/${reqId}/execute`);
      const s = r.data.status;
      setSuccess(s === 'COMPLETED' ? 'Deletion completed. Tombstone and audit record created.' : `Execution result: ${s}`);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Execution failed.');
    } finally { setActioning(null); }
  }

  const canCancelRequest = (req) => {
    const notFinal = !['COMPLETED', 'EXECUTING', 'CANCELLED'].includes(req.status);
    return canRequest && notFinal;
  };

  const canApproveRequest = (req) => {
    const approvable = ['PENDING_APPROVAL', 'REQUESTED'].includes(req.status);
    return canApprove && approvable && req.requested_by !== user?.user_id;
  };

  const canExecuteRequest = (req) => req.status === 'APPROVED' && canExecute;

  return (
    <div data-testid="deletion-requests-tab">
      <SectionHeader
        title="Deletion Requests"
        subtitle="Governed deletion workflow: request → approve → execute. Requester cannot approve their own request. CANON_LOCKED content requires CANON_OWNER + ORG_ADMIN approval."
      />
      <ErrorMsg msg={err} />
      <SuccessMsg msg={success} />

      {canRequest && (
        <div className="mb-6">
          <button
            onClick={() => setShowCreate(v => !v)}
            className="inline-flex items-center gap-2 font-heading font-semibold px-4 py-2 rounded"
            style={{ backgroundColor: '#000', color: '#03f37c', fontSize: '12px' }}
            data-testid="create-deletion-request-btn"
          >
            <FileX size={13} />
            New Deletion Request
          </button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-8 p-5 rounded border"
          style={{ borderColor: '#fca5a5', backgroundColor: '#fff5f5' }}
          data-testid="create-deletion-request-form">
          <h3 className="font-heading font-semibold text-black mb-4" style={{ fontSize: '13px', letterSpacing: '0.04em' }}>
            NEW GOVERNED DELETION REQUEST
          </h3>
          <p className="font-body text-gray-500 mb-4" style={{ fontSize: '12px' }}>
            This request will generate a dependency impact plan and fingerprint before submission.
            Requester identity is server-derived — never client-supplied.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>RESOURCE TYPE</label>
              <select value={form.target_resource_type}
                onChange={e => setForm(f => ({ ...f, target_resource_type: e.target.value }))}
                className="w-full rounded border px-3 py-2 font-body bg-white" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="dr-resource-type-select">
                <option value="artifact">artifact</option>
                <option value="claim">claim</option>
                <option value="constraint">constraint</option>
              </select>
            </div>
            <div>
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>RESOURCE ID</label>
              <input required value={form.target_resource_id}
                onChange={e => setForm(f => ({ ...f, target_resource_id: e.target.value }))}
                placeholder="e.g. art-abc123…"
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6' }}
                data-testid="dr-resource-id-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="font-heading font-medium text-gray-600 block mb-1" style={{ fontSize: '11px' }}>REASON (required)</label>
              <textarea required value={form.reason} rows={2}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Governance-authorized reason for deletion…"
                className="w-full rounded border px-3 py-2 font-body" style={{ fontSize: '13px', borderColor: '#d9dee6', resize: 'vertical' }}
                data-testid="dr-reason-input" />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="font-heading font-semibold px-4 py-2 rounded"
              style={{ backgroundColor: '#dc2626', color: '#fff', fontSize: '12px' }}
              data-testid="dr-submit-btn">
              {submitting ? 'Submitting…' : 'Submit Deletion Request'}
            </button>
            <button type="button" onClick={() => setShowCreate(false)}
              className="font-heading font-medium px-4 py-2 rounded border"
              style={{ fontSize: '12px', borderColor: '#d9dee6' }}
              data-testid="dr-cancel-create-btn">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="font-body text-gray-400" style={{ fontSize: '13px' }} data-testid="dr-loading">Loading…</p>
      ) : requests.length === 0 ? (
        <EmptyState message="No deletion requests." />
      ) : (
        <div className="space-y-4" data-testid="deletion-requests-list">
          {requests.map(req => (
            <div key={req.deletion_request_id} className="rounded border p-4"
              style={{ borderColor: req.status === 'BLOCKED_BY_HOLD' ? '#fca5a5' : '#e5e5e5',
                       backgroundColor: req.status === 'BLOCKED_BY_HOLD' ? '#fff5f5' : '#fff' }}
              data-testid={`dr-item-${req.deletion_request_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {statusBadge(req.status)}
                    {req.requires_canon_approval && (
                      <Badge label="CANON PROTECTED" color="amber" testId={`canon-protected-badge-${req.deletion_request_id}`} />
                    )}
                    <span className="font-mono text-gray-400" style={{ fontSize: '10px' }}>{req.deletion_request_id}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 mb-2">
                    <span className="font-body text-gray-600" style={{ fontSize: '12px' }}>
                      <strong>{req.target_resource_type}</strong> / <code className="font-mono">{req.target_resource_id}</code>
                    </span>
                  </div>
                  <p className="font-body text-gray-600 italic" style={{ fontSize: '12px' }}>"{req.reason}"</p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>by {req.requested_by_display}</span>
                    <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>{fmt(req.requested_at)}</span>
                    {req.approved_by_admin_display && (
                      <span className="font-body text-green-600" style={{ fontSize: '11px' }}>
                        admin: {req.approved_by_admin_display}
                      </span>
                    )}
                    {req.approved_by_canon_display && (
                      <span className="font-body text-green-600" style={{ fontSize: '11px' }}>
                        canon: {req.approved_by_canon_display}
                      </span>
                    )}
                    {req.failure_reason && (
                      <span className="font-body text-red-500" style={{ fontSize: '11px' }}>{req.failure_reason}</span>
                    )}
                  </div>
                  <ImpactPlanPanel requestId={req.deletion_request_id} />
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {canApproveRequest(req) && (
                    <button
                      onClick={() => handleApprove(req.deletion_request_id)}
                      disabled={actioning === req.deletion_request_id}
                      className="font-heading font-semibold px-3 py-1.5 rounded"
                      style={{ fontSize: '11px', backgroundColor: '#000', color: '#03f37c' }}
                      data-testid={`approve-dr-btn-${req.deletion_request_id}`}
                    >
                      {actioning === req.deletion_request_id ? '…' : 'Approve'}
                    </button>
                  )}
                  {canExecuteRequest(req) && (
                    <button
                      onClick={() => handleExecute(req.deletion_request_id)}
                      disabled={actioning === req.deletion_request_id}
                      className="font-heading font-semibold px-3 py-1.5 rounded"
                      style={{ fontSize: '11px', backgroundColor: '#dc2626', color: '#fff' }}
                      data-testid={`execute-dr-btn-${req.deletion_request_id}`}
                    >
                      {actioning === req.deletion_request_id ? '…' : 'Execute'}
                    </button>
                  )}
                  {canCancelRequest(req) && (
                    <button
                      onClick={() => handleCancel(req.deletion_request_id)}
                      disabled={actioning === req.deletion_request_id}
                      className="font-heading font-medium px-3 py-1.5 rounded border"
                      style={{ fontSize: '11px', borderColor: '#d9dee6', color: '#6b7280' }}
                      data-testid={`cancel-dr-btn-${req.deletion_request_id}`}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Tombstones ──────────────────────────────────────────────────────────

function TombstonesTab() {
  const [tombstones, setTombstones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/lifecycle/tombstones')
      .then(r => setTombstones(r.data.tombstones || []))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load tombstones.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="tombstones-tab">
      <SectionHeader
        title="Lifecycle Tombstones"
        subtitle="Read-only governance evidence after deletion. Tombstones contain the content fingerprint, display name, and audit reference only — no deleted governed content."
      />
      {err && <ErrorMsg msg={err} />}
      {loading ? (
        <p className="font-body text-gray-400" style={{ fontSize: '13px' }} data-testid="tombstones-loading">Loading…</p>
      ) : tombstones.length === 0 ? (
        <EmptyState message="No lifecycle tombstones. Tombstones appear here after governed deletion is executed." />
      ) : (
        <div className="space-y-3" data-testid="tombstones-list">
          {tombstones.map(ts => (
            <div key={ts.tombstone_id} className="rounded border p-4" style={{ borderColor: '#e5e5e5' }} data-testid={`tombstone-item-${ts.tombstone_id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge label="TOMBSTONE" color="gray" />
                <span className="font-heading font-semibold text-black" style={{ fontSize: '13px' }}>
                  {ts.original_title || ts.deleted_resource_id}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <span className="font-heading text-gray-400 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>TYPE</span>
                  <span className="font-mono text-black" style={{ fontSize: '12px' }}>{ts.deleted_resource_type}</span>
                </div>
                <div>
                  <span className="font-heading text-gray-400 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>RESOURCE ID</span>
                  <span className="font-mono text-black" style={{ fontSize: '12px' }}>{ts.deleted_resource_id}</span>
                </div>
                <div>
                  <span className="font-heading text-gray-400 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>SHA-256 FINGERPRINT</span>
                  <span className="font-mono text-gray-600" style={{ fontSize: '10px' }} data-testid={`tombstone-fingerprint-${ts.tombstone_id}`}>
                    {ts.content_fingerprint ? `${ts.content_fingerprint.slice(0, 16)}…` : '—'}
                  </span>
                </div>
                <div>
                  <span className="font-heading text-gray-400 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>DELETED</span>
                  <span className="font-body text-black" style={{ fontSize: '12px' }}>{fmt(ts.deleted_at)}</span>
                </div>
                <div>
                  <span className="font-heading text-gray-400 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>EXECUTOR</span>
                  <span className="font-body text-black" style={{ fontSize: '12px' }}>{ts.executor_display}</span>
                </div>
                <div>
                  <span className="font-heading text-gray-400 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>DELETION REQUEST</span>
                  <span className="font-mono text-gray-600" style={{ fontSize: '11px' }}>{ts.deletion_request_id}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Lifecycle Page ──────────────────────────────────────────────────────

const TABS = [
  { id: 'policies',  label: 'Retention Policies', icon: Shield },
  { id: 'holds',     label: 'Legal Holds',         icon: Lock },
  { id: 'requests',  label: 'Deletion Requests',   icon: FileX },
  { id: 'tombstones', label: 'Tombstones',          icon: Archive },
];

export default function Lifecycle() {
  const [activeTab, setActiveTab] = useState('policies');

  return (
    <div data-testid="lifecycle-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={20} style={{ color: '#a3a3a3' }} />
          <h1 className="page-heading" data-testid="lifecycle-heading">Lifecycle Governance</h1>
        </div>
        <p className="font-body text-gray-500" style={{ fontSize: '14px' }}>
          Governed data-lifecycle control plane. Retention policy identifies eligibility — it does NOT authorize deletion.
          All destructive actions require human authorization and separation of duties.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b pb-0" style={{ borderColor: '#e5e5e5' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-4 py-2.5 font-heading font-medium transition-colors"
              style={{
                fontSize: '12px',
                letterSpacing: '0.04em',
                borderBottom: active ? '2px solid #000' : '2px solid transparent',
                color: active ? '#000' : '#6b7280',
                marginBottom: '-1px',
              }}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={13} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'policies'   && <RetentionPoliciesTab />}
        {activeTab === 'holds'      && <LegalHoldsTab />}
        {activeTab === 'requests'   && <DeletionRequestsTab />}
        {activeTab === 'tombstones' && <TombstonesTab />}
      </div>
    </div>
  );
}
